/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { NavFeaturedLink } from '@/lib/navigation/types';

/** SCO-NAV-03 A caps the list at five. */
export const MAX_FEATURED_LINKS = 5;

function isLink(value: unknown): value is NavFeaturedLink {
    if (typeof value !== 'object' || value === null) return false;
    const { text, url } = value as Record<string, unknown>;
    return typeof text === 'string' && text.length > 0 && typeof url === 'string' && url.length > 0;
}

/**
 * Parses the `c_featuredLinks` JSON blob a merchandiser types into Business Manager.
 * Fail-soft by design: a malformed value drops the column rather than breaking the header,
 * so callers holding a logger should warn when a non-empty input yields nothing.
 */
export function parseFeaturedLinks(raw: unknown): NavFeaturedLink[] {
    if (typeof raw !== 'string' || raw.trim().length === 0) {
        return [];
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return [];
    }

    if (!Array.isArray(parsed)) {
        return [];
    }

    return parsed.filter(isLink).slice(0, MAX_FEATURED_LINKS);
}
